import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Save, Trash2, UserCheck, Users as UsersIcon, X } from "lucide-react";
import { Seo } from "@/components/Seo";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { Input, Label } from "@/components/ui/input";
import {
  DEPARTMENT_LABEL,
  ROLE_LABEL,
  STAFF_LEVEL_LABEL,
  canManageUsers,
  type AppUser,
  type Department,
  type Role,
  type StaffLevel,
} from "@/features/auth/types";
import { useAuth } from "@/features/auth/useAuth";
import {
  createUserAccount,
  deleteUserAccount,
  listUserProfiles,
  updateUserProfile,
  type CreatedUser,
  type UserProfile,
} from "@/features/users/repo";

const filters: Array<"all" | Role> = ["all", "internal", "customer", "driver", "carrier"];

const roleBadge: Record<Role, "default" | "success" | "warning"> = {
  internal: "success",
  customer: "default",
  driver: "warning",
  carrier: "warning",
};

const DEPARTMENTS = Object.keys(DEPARTMENT_LABEL) as Department[];

/** External roles a department is allowed to manage (mirrors the edge function). */
function ownedExternalRoles(dept: Department | null | undefined): Role[] {
  return dept === "dispatch" ? ["driver", "carrier"] : [];
}

/** Roles the current user is allowed to create. */
function creatableRoles(user: AppUser | null): Role[] {
  if (user?.staffLevel === "admin") return ["internal", "customer", "driver", "carrier"];
  if (user?.staffLevel === "manager") return ["internal", ...ownedExternalRoles(user.department)];
  return [];
}

export default function InternalUsers() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [filter, setFilter] = useState<"all" | Role>("all");
  const [editing, setEditing] = useState<UserProfile | null>(null);
  const [creating, setCreating] = useState(false);
  const [createdInfo, setCreatedInfo] = useState<CreatedUser | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [assigningDriverId, setAssigningDriverId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteUser, setPendingDeleteUser] = useState<UserProfile | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      setProfiles(await listUserProfiles());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load user profiles.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const visibleProfiles = useMemo(
    () => profiles.filter((profile) => filter === "all" || profile.role === filter),
    [profiles, filter],
  );

  const dispatchers = useMemo(
    () =>
      profiles
        .filter((profile) => profile.role === "internal" && profile.department === "dispatch")
        .sort((a, b) => (a.fullName || a.email).localeCompare(b.fullName || b.email)),
    [profiles],
  );

  const drivers = useMemo(
    () =>
      profiles
        .filter((profile) => profile.role === "driver")
        .sort((a, b) => (a.fullName || a.email).localeCompare(b.fullName || b.email)),
    [profiles],
  );

  const dispatcherById = useMemo(
    () => new Map(dispatchers.map((dispatcher) => [dispatcher.id, dispatcher])),
    [dispatchers],
  );

  const showDriverAssignments =
    user?.staffLevel === "admin" || (user?.staffLevel === "manager" && user.department === "dispatch");

  const counts = useMemo(
    () =>
      profiles.reduce(
        (acc, profile) => {
          acc[profile.role] += 1;
          return acc;
        },
        { customer: 0, driver: 0, carrier: 0, internal: 0 } satisfies Record<Role, number>,
      ),
    [profiles],
  );

  async function handleSave() {
    if (!editing) return;
    if (!editing.email.trim()) {
      setError("Email is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateUserProfile(editing);
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save user profile.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreate(input: {
    email: string;
    fullName: string;
    role: Role;
    staffLevel: StaffLevel | null;
    department: Department | null;
    password: string;
  }) {
    setSaving(true);
    setError(null);
    try {
      const created = await createUserAccount({
        email: input.email,
        fullName: input.fullName,
        role: input.role,
        staffLevel: input.role === "internal" ? input.staffLevel : null,
        department: input.role === "internal" ? input.department : null,
        password: input.password || undefined,
      });
      setCreating(false);
      setCreatedInfo(created);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create user.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(profile: UserProfile) {
    setPendingDeleteUser(profile);
  }

  async function confirmDeleteUser() {
    if (!pendingDeleteUser) return;
    setDeletingId(pendingDeleteUser.id);
    setError(null);
    try {
      await deleteUserAccount(pendingDeleteUser.id);
      setPendingDeleteUser(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete user.");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleAssignDriver(driver: UserProfile, dispatcherId: string) {
    setAssigningDriverId(driver.id);
    setError(null);
    try {
      await updateUserProfile({
        ...driver,
        dispatcherId: dispatcherId || null,
      });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not assign driver.");
    } finally {
      setAssigningDriverId(null);
    }
  }

  if (!canManageUsers(user)) {
    return (
      <>
        <Seo title="Users" noindex />
        <h1 className="text-2xl font-bold">Users & roles</h1>
        <p className="mt-4 rounded-lg border border-dashed border-brand-200 p-6 text-sm text-brand-500">
          User management is available to admins and department managers. Ask your
          department manager or an admin if you need an account created or changed.
        </p>
      </>
    );
  }

  const isManager = user?.staffLevel === "manager";
  const scopeNote = isManager
    ? `You manage the ${user?.department ? DEPARTMENT_LABEL[user.department] : ""} department.`
    : "You are an admin - you manage all departments and users.";

  return (
    <>
      <Seo title="Users" noindex />
      <DemoBanner />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Users & roles</h1>
          <p className="mt-1 max-w-2xl text-sm text-brand-500">
            Create logins, assign roles, and map users to external system IDs. {scopeNote}
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <Button variant="accent" onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> New user
          </Button>
          <Card className="w-full sm:max-w-xs">
            <CardContent className="flex items-center gap-3 pt-6">
              <span className="rounded-full bg-accent-500/10 p-3 text-accent-600">
                <UsersIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-2xl font-bold">{profiles.length}</p>
                <p className="text-xs text-brand-500">Known user profiles</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {createdInfo && (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-900">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold">Account created for {createdInfo.email}</p>
              {createdInfo.password ? (
                <p className="mt-1">
                  Temporary password (shown once - copy and share securely):{" "}
                  <code className="break-all rounded bg-white px-2 py-0.5 font-mono text-green-800">
                    {createdInfo.password}
                  </code>
                </p>
              ) : (
                <p className="mt-1">The user can sign in with the password you set.</p>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setCreatedInfo(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-3 rounded-xl border border-brand-100 bg-white p-4 sm:grid-cols-[minmax(220px,320px)_1fr] sm:items-end">
        <div>
          <Label htmlFor="user-role-filter">Filter users</Label>
          <select
            id="user-role-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as "all" | Role)}
            className="mt-1 flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
          >
            {filters.map((value) => (
              <option key={value} value={value}>
                {value === "all" ? `All users (${profiles.length})` : `${ROLE_LABEL[value]} (${counts[value]})`}
              </option>
            ))}
          </select>
        </div>
        <p className="text-sm text-brand-500">
          {isManager
            ? "Managers see only their own department members and any external roles their department owns."
            : "Admins see the full company user list and can create managers, members, customers, drivers, and owner-operators."}
        </p>
      </div>

      {error && (
        <FeedbackMessage tone="error" className="mt-4">
          {error}
        </FeedbackMessage>
      )}

      {showDriverAssignments && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <UserCheck className="h-5 w-5 text-accent-600" />
                  Assigned drivers
                </CardTitle>
                <p className="mt-1 text-sm text-brand-500">
                  See which driver is under which dispatcher and reassign drivers as dispatch changes.
                </p>
              </div>
              <Badge variant="accent">
                {drivers.filter((driver) => driver.dispatcherId).length}/{drivers.length} assigned
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="grid gap-3">
                <CardSkeleton rows={2} />
                <CardSkeleton rows={2} />
              </div>
            ) : drivers.length === 0 ? (
              <p className="rounded-lg border border-dashed border-brand-200 p-4 text-sm text-brand-500">
                No driver accounts are available yet.
              </p>
            ) : (
              <div className="grid gap-3">
                {drivers.map((driver) => {
                  const assignedDispatcher = driver.dispatcherId
                    ? dispatcherById.get(driver.dispatcherId)
                    : null;
                  return (
                    <div
                      key={driver.id}
                      className="grid gap-3 rounded-lg border border-brand-100 p-4 md:grid-cols-[1fr_1fr_auto] md:items-center"
                    >
                      <div className="min-w-0">
                        <p className="break-words font-semibold text-brand-900">
                          {driver.fullName || driver.email}
                        </p>
                        <p className="break-all text-sm text-brand-500">{driver.email}</p>
                      </div>
                      <div className="min-w-0 text-sm">
                        <p className="text-xs uppercase tracking-wide text-brand-400">
                          Current dispatcher
                        </p>
                        <p className="mt-1 break-words font-medium text-brand-800">
                          {assignedDispatcher?.fullName || assignedDispatcher?.email || "Unassigned"}
                        </p>
                      </div>
                      <div className="min-w-0 md:min-w-52">
                        <Label htmlFor={`dispatcher-${driver.id}`} className="sr-only">
                          Assign dispatcher
                        </Label>
                        <select
                          id={`dispatcher-${driver.id}`}
                          value={driver.dispatcherId ?? ""}
                          disabled={assigningDriverId === driver.id || dispatchers.length === 0}
                          onChange={(e) => void handleAssignDriver(driver, e.target.value)}
                          className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm disabled:opacity-60"
                        >
                          <option value="">Unassigned</option>
                          {dispatchers.map((dispatcher) => (
                            <option key={dispatcher.id} value={dispatcher.id}>
                              {dispatcher.fullName || dispatcher.email}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="mt-6 grid gap-3">
        {loading ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : visibleProfiles.length === 0 ? (
          <EmptyState
            title="No users found"
            body="Try another role filter, or create a new account if this team member should have access."
          />
        ) : (
          visibleProfiles.map((profile) => (
            <Card key={profile.id}>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="break-words text-base">
                      {profile.fullName || profile.email}
                    </CardTitle>
                    <p className="break-all text-sm text-brand-500">{profile.email}</p>
                  </div>
                  <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
                    <Badge variant={roleBadge[profile.role]}>
                      {ROLE_LABEL[profile.role]}
                    </Badge>
                    {profile.role === "internal" && profile.staffLevel && (
                      <Badge variant="accent">{STAFF_LEVEL_LABEL[profile.staffLevel]}</Badge>
                    )}
                    {profile.role === "internal" && profile.department && (
                      <Badge variant="default">{DEPARTMENT_LABEL[profile.department]}</Badge>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setEditing(profile)}>
                      <Pencil className="mr-1 h-4 w-4" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      disabled={profile.id === user?.id || deletingId === profile.id}
                      title={profile.id === user?.id ? "You cannot delete your own account" : "Delete account"}
                      onClick={() => handleDelete(profile)}
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      {deletingId === profile.id ? "Deleting..." : "Delete"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 text-sm text-brand-600 md:grid-cols-3">
                  <Mapping label="QuickManage customer" value={profile.quickmanageCustomerId} />
                  <Mapping label="Samsara driver" value={profile.samsaraDriverId} />
                  <Mapping
                    label={profile.role === "driver" ? "Assigned dispatcher" : "Carrier ID"}
                    value={
                      profile.role === "driver"
                        ? dispatcherById.get(profile.dispatcherId ?? "")?.fullName ||
                          dispatcherById.get(profile.dispatcherId ?? "")?.email ||
                          ""
                        : profile.carrierId
                    }
                  />
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {editing && (
        <UserEditor
          profile={editing}
          currentUser={user}
          saving={saving}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {creating && (
        <CreateUser
          currentUser={user}
          saving={saving}
          onCancel={() => setCreating(false)}
          onCreate={handleCreate}
        />
      )}
      <ConfirmDialog
        open={!!pendingDeleteUser}
        title="Delete user account?"
        body={`Delete the account for ${pendingDeleteUser?.email ?? "this user"}? This removes their login and cannot be undone.`}
        confirmLabel="Delete account"
        tone="danger"
        busy={!!deletingId}
        onConfirm={() => void confirmDeleteUser()}
        onCancel={() => setPendingDeleteUser(null)}
      />
    </>
  );
}

function CreateUser({
  currentUser,
  saving,
  onCancel,
  onCreate,
}: {
  currentUser: AppUser | null;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: {
    email: string;
    fullName: string;
    role: Role;
    staffLevel: StaffLevel | null;
    department: Department | null;
    password: string;
  }) => void;
}) {
  const roleOptions = creatableRoles(currentUser);
  const isAdmin = currentUser?.staffLevel === "admin";

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<Role>(roleOptions[0] ?? "internal");
  // Managers can only create members in their own department.
  const [staffLevel, setStaffLevel] = useState<StaffLevel>(isAdmin ? "member" : "member");
  const [department, setDepartment] = useState<Department>(
    isAdmin ? "dispatch" : currentUser?.department ?? "dispatch",
  );
  const [password, setPassword] = useState("");

  // For managers the level/department are fixed to their own scope.
  const effectiveLevel: StaffLevel = isAdmin ? staffLevel : "member";
  const effectiveDept: Department = isAdmin ? department : (currentUser?.department ?? "dispatch");

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="break-words text-xl font-bold">Create user account</h2>
            <p className="mt-1 text-sm text-brand-500">
              Creates a real login. Leave the password blank to auto-generate one you
              can share - it is shown only once.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-5 grid gap-4">
          <div>
            <Label htmlFor="new-email">Email</Label>
            <Input
              id="new-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@bullandbearco.com"
            />
          </div>
          <div>
            <Label htmlFor="new-name">Full name</Label>
            <Input id="new-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="new-role">Role</Label>
              <select
                id="new-role"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
              >
                {roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="new-password">Password (optional)</Label>
              <Input
                id="new-password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Auto-generate if blank"
              />
            </div>
          </div>

          {role === "internal" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="new-level">Staff tier</Label>
                <select
                  id="new-level"
                  value={effectiveLevel}
                  disabled={!isAdmin}
                  onChange={(e) => setStaffLevel(e.target.value as StaffLevel)}
                  className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm disabled:opacity-60"
                >
                  {(isAdmin ? (["admin", "manager", "member"] as StaffLevel[]) : (["member"] as StaffLevel[])).map(
                    (lvl) => (
                      <option key={lvl} value={lvl}>
                        {STAFF_LEVEL_LABEL[lvl]}
                      </option>
                    ),
                  )}
                </select>
                {!isAdmin && (
                  <p className="mt-1 text-xs text-brand-400">Managers can add members only.</p>
                )}
              </div>
              <div>
                <Label htmlFor="new-dept">Department</Label>
                <select
                  id="new-dept"
                  value={effectiveLevel === "admin" ? "" : effectiveDept}
                  disabled={!isAdmin || effectiveLevel === "admin"}
                  onChange={(e) => setDepartment(e.target.value as Department)}
                  className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm disabled:opacity-60"
                >
                  {effectiveLevel === "admin" && <option value="">All (admin)</option>}
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {DEPARTMENT_LABEL[d]}
                    </option>
                  ))}
                </select>
                {!isAdmin && (
                  <p className="mt-1 text-xs text-brand-400">
                    Locked to {DEPARTMENT_LABEL[effectiveDept]}.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" className="w-full sm:w-auto" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="accent"
            className="w-full sm:w-auto"
            disabled={saving}
            onClick={() =>
              onCreate({
                email,
                fullName,
                role,
                staffLevel: effectiveLevel,
                department: effectiveLevel === "admin" ? null : effectiveDept,
                password,
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" /> {saving ? "Creating..." : "Create account"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Mapping({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-brand-50 px-3 py-2">
      <p className="text-xs uppercase tracking-wide text-brand-400">{label}</p>
      <p className="mt-1 break-words font-medium text-brand-800">{value || "Not mapped"}</p>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-dashed border-brand-200 bg-white p-6 text-center">
      <p className="font-medium text-brand-900">{title}</p>
      <p className="mt-1 text-sm text-brand-500">{body}</p>
    </div>
  );
}

function UserEditor({
  profile,
  currentUser,
  saving,
  onChange,
  onCancel,
  onSave,
}: {
  profile: UserProfile;
  currentUser: AppUser | null;
  saving: boolean;
  onChange: (profile: UserProfile) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const editingSelf = profile.id === currentUser?.id;
  const isAdmin = currentUser?.staffLevel === "admin";
  // Managers can't change role/tier/department - only admins can.
  const lockOrg = !isAdmin || editingSelf;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="break-words text-xl font-bold">Edit user profile</h2>
            <p className="mt-1 text-sm text-brand-500">
              Role controls which portal the user can access. External IDs connect
              the login to QuickManage, Samsara, or carrier records later.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="profile-name">Full name</Label>
            <Input
              id="profile-name"
              value={profile.fullName}
              onChange={(e) => onChange({ ...profile, fullName: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="profile-email">Email</Label>
            <Input
              id="profile-email"
              type="email"
              value={profile.email}
              onChange={(e) => onChange({ ...profile, email: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="profile-role">Role</Label>
            <select
              id="profile-role"
              value={profile.role}
              disabled={lockOrg}
              onChange={(e) => onChange({ ...profile, role: e.target.value as Role })}
              className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm disabled:opacity-50"
            >
              {(Object.keys(ROLE_LABEL) as Role[]).map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABEL[role]}
                </option>
              ))}
            </select>
            {editingSelf && (
              <p className="mt-1 text-xs text-brand-400">
                To avoid locking yourself out, ask another admin to change your role or tier.
              </p>
            )}
          </div>
          {profile.role === "internal" && (
            <>
              <div>
                <Label htmlFor="profile-level">Staff tier</Label>
                <select
                  id="profile-level"
                  value={profile.staffLevel ?? "member"}
                  disabled={lockOrg}
                  onChange={(e) => onChange({ ...profile, staffLevel: e.target.value as StaffLevel })}
                  className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm disabled:opacity-50"
                >
                  {(["admin", "manager", "member"] as StaffLevel[]).map((lvl) => (
                    <option key={lvl} value={lvl}>
                      {STAFF_LEVEL_LABEL[lvl]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="profile-dept">Department</Label>
                <select
                  id="profile-dept"
                  value={profile.department ?? ""}
                  disabled={lockOrg || profile.staffLevel === "admin"}
                  onChange={(e) =>
                    onChange({ ...profile, department: (e.target.value || null) as Department | null })
                  }
                  className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm disabled:opacity-50"
                >
                  <option value="">{profile.staffLevel === "admin" ? "All (admin)" : "Select..."}</option>
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {DEPARTMENT_LABEL[d]}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="profile-quickmanage">QuickManage customer ID</Label>
            <Input
              id="profile-quickmanage"
              value={profile.quickmanageCustomerId}
              onChange={(e) =>
                onChange({ ...profile, quickmanageCustomerId: e.target.value })
              }
            />
          </div>
          <div>
            <Label htmlFor="profile-samsara">Samsara driver ID</Label>
            <Input
              id="profile-samsara"
              value={profile.samsaraDriverId}
              onChange={(e) => onChange({ ...profile, samsaraDriverId: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="profile-carrier">Carrier ID</Label>
            <Input
              id="profile-carrier"
              value={profile.carrierId}
              onChange={(e) => onChange({ ...profile, carrierId: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" className="w-full sm:w-auto" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="accent" className="w-full sm:w-auto" onClick={onSave} disabled={saving}>
            <Save className="mr-1 h-4 w-4" /> {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
